/**
 * LinguistPro Transcription Service — v5.0.0
 * Client-side service that communicates with the /api/transcribe endpoint.
 *
 * Replaces geminiService.ts — no more 5-phase Gemini pipeline.
 * Deepgram handles STT + diarisation in a single API call.
 * Intelligence synthesis still uses /api/generate-intelligence (Gemini).
 */

import { API_CONFIG } from '../config/constants';
import type { LogCallback, VideoSceneDescription } from '../types';

// ── Custom error with HTTP status ─────────────────────────────────────

class ApiError extends Error {
  status: number;
  code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

/**
 * Thrown when a long AssemblyAI job is still running after the client's polling budget.
 * The audio was NOT resubmitted and the job keeps running server-side (its 'processing'
 * row is in History). Callers must not fall back to another engine — that would be a
 * second transcription of the same file and a second charge.
 */
export class TranscriptionStillProcessingError extends Error {
  readonly transcriptId: string;
  constructor(transcriptId: string, message: string) {
    super(message);
    this.name = 'TranscriptionStillProcessingError';
    this.transcriptId = transcriptId;
  }
}

/** Shown when AssemblyAI reports status=error for a job. Nothing was saved, so nothing was charged. */
export const TRANSCRIPTION_FAILED_TEXT =
  'This transcription failed at the provider. You have not been charged. Please try again.';

/**
 * Thrown when /api/transcribe-assemblyai answers 422 TRANSCRIPTION_FAILED: the provider
 * job is dead. Polling stops; callers must not present it as still running.
 */
export class TranscriptionFailedError extends Error {
  readonly providerMessage?: string;
  constructor(providerMessage?: string) {
    super(TRANSCRIPTION_FAILED_TEXT);
    this.name = 'TranscriptionFailedError';
    this.providerMessage = providerMessage;
  }
}

function isJobFailed(err: unknown): err is ApiError {
  return err instanceof ApiError && err.status === 422 && err.code === 'TRANSCRIPTION_FAILED';
}

/** Client polling budget for a long AssemblyAI job, and its backoff (5 s → 15 s). */
export const ASSEMBLYAI_POLL_MAX_MS = 30 * 60 * 1000;
const POLL_MAX_CONSECUTIVE_ERRORS = 5;
export const STILL_PROCESSING_AFTER_ERRORS_TEXT =
  'We could not check on this recording just now, but it is still being transcribed. It has not been resubmitted or charged again — ' +
  'it will appear in History when it finishes (use Complete on the Processing row to fetch it).';
const POLL_DELAY_START_MS = 5_000;
const POLL_DELAY_MAX_MS = 15_000;
export const STILL_TRANSCRIBING_TEXT =
  'Still transcribing a long file — you can leave this page; it will appear in History.';

/**
 * Thrown when /api/transcribe-gemini reports GEMINI_UNAVAILABLE (503) — the
 * server classified the underlying failure as a Google auth rejection
 * (401/403 UNAUTHENTICATED etc), which cannot succeed by retrying. Typed
 * separately from ApiError so callers can distinguish "Gemini's key is dead,
 * open the circuit breaker now" from an ordinary transient failure.
 */
export class GeminiUnavailableError extends Error {
  status: number;
  code = 'GEMINI_UNAVAILABLE';
  reason: string;
  fallbackEngine: string;
  retryable = false as const;
  constructor(fallbackEngine: string, reason: string, status = 503) {
    super(`Gemini is unavailable (${reason}) — fallback engine: ${fallbackEngine}`);
    this.name = 'GeminiUnavailableError';
    this.status = status;
    this.reason = reason;
    this.fallbackEngine = fallbackEngine;
  }
}

// ── Types ─────────────────────────────────────────────────────────────

export interface TranscriptionWord {
  word: string;
  start: number;
  end: number;
  confidence: number;
  speaker?: number;
  punctuated_word?: string;
}

export interface TranscriptionUtterance {
  start: number;
  end: number;
  transcript: string;
  speaker: number;
  confidence: number;
  words: TranscriptionWord[];
}

export interface TranscribeResult {
  transcript: string;
  utterances: TranscriptionUtterance[];
  speakers: number;
  duration: number;
  engine: string;
  language: string;
  confidence: number;
  formatted: string;
  /** A/B/C/D quality grade computed server-side by computeQualityScore() */
  quality_grade?: string;
  /** Visual scene descriptions — only present for video transcription jobs */
  sceneDescriptions?: VideoSceneDescription[];
  /** AssemblyAI job id — set only when the job outlived the first call (a stub row exists). */
  assemblyaiId?: string;
}

export type TranscriptionEngine = 'deepgram' | 'groq' | 'gemini' | 'auto';

export interface MultilingualSummaryDecision {
  text: string;
  by: string;
  at: string | null;
  impact: 'High' | 'Medium' | 'Low';
}

export interface MultilingualSummaryAction {
  task: string;
  assignee: string | null;
  due_date: string | null;
  priority: 'High' | 'Medium' | 'Low';
  at: string | null;
  context: string;
}

export interface MultilingualSummaryResult {
  language_detected: string;
  overview: string;
  participants: string[];
  decisions: MultilingualSummaryDecision[];
  actions: MultilingualSummaryAction[];
  follow_up: string[];
  summary_markdown: string;
}

// ── Service ───────────────────────────────────────────────────────────

export class TranscriptionService {
  private authToken: string | null = null;

  /** Set the JWT auth token for authenticated API calls */
  setAuthToken(token: string | null) {
    this.authToken = token;
  }

  /**
   * Execute API call with exponential backoff retry logic
   */
  private async callWithRetry<T>(
    fn: () => Promise<T>,
    onStatus: LogCallback,
    retries: number = Number(API_CONFIG.MAX_RETRIES),
    delay: number = Number(API_CONFIG.INITIAL_RETRY_DELAY)
  ): Promise<T> {
    try {
      return await fn();
    } catch (error: unknown) {
      const errorObj = error as { status?: number; code?: string; message?: string };

      // Non-retryable errors — fail immediately, don't waste time retrying
      // 402 = Insufficient balance — no point retrying, user must top up first
      // 408 = Request timeout — the work couldn't fit in the deadline; retry will not help
      // 413 = Payload too large — structural, not transient
      // 422 = Unprocessable entity — validation failure, same request will fail the same way
      // 503 = Service not configured (e.g. ASSEMBLYAI_API_KEY missing) — retrying won't help
      // 504 = Gateway/function timeout — the server ran out of its time budget on a large
      //       prompt; retrying with the same input produces the same timeout and compounds
      //       the UX delay (seen in the wild: 4× 504 = ~200 s of dead waiting for the user)
      const NON_RETRYABLE = [400, 401, 402, 403, 408, 413, 422, 503, 504];
      if (errorObj.status && NON_RETRYABLE.includes(errorObj.status)) {
        throw error;
      }

      // App-level timeout messages surfaced from intelligence.ts (e.g. the Gemini
      // race-loss path that returns a 200 with an error body) — also non-retryable.
      const errMsgLower = (errorObj.message ?? '').toLowerCase();
      if (errMsgLower.includes('timed out') || errMsgLower.includes('timeout')) {
        throw error;
      }

      const errorStr = String(error).toLowerCase();
      const isQuota =
        errorObj.status === 429 ||
        errorStr.includes('quota') ||
        errorStr.includes('limit') ||
        errorStr.includes('rate');

      if (retries > 0 && isQuota) {
        onStatus(
          `Rate limited. Cooling down (${API_CONFIG.QUOTA_COOLING / 1000}s)...`,
          'warning'
        );
        await new Promise((resolve) => setTimeout(resolve, API_CONFIG.QUOTA_COOLING));
        return this.callWithRetry(fn, onStatus, retries - 1, delay * API_CONFIG.RETRY_MULTIPLIER);
      }

      if (retries > 0) {
        const attempt = API_CONFIG.MAX_RETRIES - retries + 1;
        onStatus(`Transcription engine busy — resuming in ${(delay / 1000).toFixed(0)}s (attempt ${attempt} of ${API_CONFIG.MAX_RETRIES})...`, 'info');
        await new Promise((resolve) => setTimeout(resolve, delay));
        return this.callWithRetry(fn, onStatus, retries - 1, delay * API_CONFIG.RETRY_MULTIPLIER);
      }

      throw error;
    }
  }

  /**
   * POST request helper
   */
  private async postRequest<T>(endpoint: string, body: Record<string, unknown>): Promise<T> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }

    const response = await fetch(`/api/${endpoint}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      const errBody = errorData as { error?: string; code?: string; reason?: string; fallbackEngine?: string };

      // GEMINI_UNAVAILABLE (503) — the server has already classified this as a
      // non-retryable Gemini auth rejection. Surface it as its own type so the
      // caller can open the circuit breaker on the FIRST failure instead of
      // treating it like any other error.
      if (errBody.error === 'GEMINI_UNAVAILABLE') {
        throw new GeminiUnavailableError(
          errBody.fallbackEngine || 'assemblyai',
          errBody.reason || 'unknown',
          response.status
        );
      }

      const msg = errBody.error || `HTTP ${response.status}`;
      const code = errBody.code;
      throw new ApiError(msg, response.status, code);
    }

    return response.json();
  }

  /**
   * Transcribe audio — single API call to Deepgram/Groq via our serverless function.
   * This replaces the old 5-phase pipeline (speaker-profile → transcribe-segment
   * → analyze-gaps → audit-transcript).
   */
  async transcribe(
    audioBase64: string,
    mimeType: string,
    onStatus: LogCallback,
    options: {
      language?: string;
      engine?: TranscriptionEngine;
      diarize?: boolean;
    } = {}
  ): Promise<TranscribeResult> {
    const { language = 'en', engine = 'auto', diarize = true } = options;

    return this.callWithRetry(async () => {
      const result = await this.postRequest<TranscribeResult>('transcribe', {
        audioBase64,
        mimeType,
        language,
        engine,
        diarize,
      });
      return result;
    }, onStatus);
  }

  /**
   * Transcribe audio from a remote URL.
   */
  async transcribeFromUrl(
    audioUrl: string,
    onStatus: LogCallback,
    options: {
      language?: string;
      engine?: TranscriptionEngine;
      diarize?: boolean;
    } = {}
  ): Promise<TranscribeResult> {
    const { language = 'en', engine = 'auto', diarize = true } = options;

    return this.callWithRetry(async () => {
      const result = await this.postRequest<TranscribeResult>('transcribe', {
        audioUrl,
        language,
        engine,
        diarize,
      });
      return result;
    }, onStatus);
  }

  /**
   * Transcribe audio via Gemini — multilingual with speaker diarisation.
   * Uses /api/transcribe-gemini endpoint.
   *
   * `contextHint` (optional) carries the previous chunk's tail for sequential
   * chunked transcription of long recordings — it is prepended to the prompt
   * server-side so speaker numbering stays consistent across chunk boundaries
   * and the overlapping audio is skipped. See src/utils/chunkAudio.ts.
   */
  async transcribeWithGemini(
    audioBase64: string,
    mimeType: string,
    onStatus: LogCallback,
    contextHint?: string,
    language?: string
  ): Promise<TranscribeResult> {
    return this.callWithRetry(async () => {
      const result = await this.postRequest<TranscribeResult>('transcribe-gemini', {
        audioBase64,
        mimeType,
        ...(contextHint ? { contextHint } : {}),
        ...(language ? { language } : {}),
      });
      return result;
    }, onStatus);
  }

  /**
   * Transcribe audio via ElevenLabs Scribe (model_id=scribe_v1) — Scribe-first
   * engine for South African indigenous languages (zu/xh/st/tn/ve). Validated
   * ~3× more accurate than Gemini on isiXhosa (2026-06-27). The server forces
   * the ISO 639-3 language_code (auto-detect mislabels isiXhosa).
   *
   * Served by /api/transcribe-gemini behind `provider: 'elevenlabs'` (Scribe is
   * a library, not its own serverless function — Vercel Hobby caps at 12). On any
   * failure (non-2xx, empty text, unsupported language) it throws so the caller
   * can fall back to Gemini.
   */
  async transcribeWithElevenLabs(
    audioBase64: string,
    mimeType: string,
    onStatus: LogCallback,
    language: string
  ): Promise<TranscribeResult> {
    return this.callWithRetry(async () => {
      const result = await this.postRequest<TranscribeResult>('transcribe-gemini', {
        audioBase64,
        mimeType,
        language,
        provider: 'elevenlabs',
      });
      return result;
    }, onStatus);
  }

  /**
   * Transcribe audio from a remote URL via ElevenLabs Scribe.
   * Scribe-first engine for SA-indigenous languages; falls back to Gemini on failure.
   */
  async transcribeFromUrlWithElevenLabs(
    audioUrl: string,
    onStatus: LogCallback,
    language: string
  ): Promise<TranscribeResult> {
    return this.callWithRetry(async () => {
      const result = await this.postRequest<TranscribeResult>('transcribe-gemini', {
        audioUrl,
        language,
        provider: 'elevenlabs',
      });
      return result;
    }, onStatus);
  }

  /**
   * Transcribe audio from URL via AssemblyAI Universal-2.
   * Primary engine for large files — handles Afrikaans + English code-switching natively.
   * Supports 10-hour audio, 5GB files. No chunking needed.
   *
   * The Vercel function (60 s) often outlives its polling budget on long audio and answers
   * 202 + transcriptId. We then keep polling with ONLY that transcriptId (backoff 5 s → 15 s,
   * 30 min cap). The audio URL is never POSTed again, so no new AssemblyAI job and no new
   * charge is created. Past the cap we throw TranscriptionStillProcessingError.
   */
  async transcribeFromUrlWithAssemblyAI(
    audioUrl: string,
    onStatus: LogCallback,
    language?: string,
    options: { contentHash?: string; signal?: AbortSignal } = {}
  ): Promise<TranscribeResult> {
    type Poll = TranscribeResult | { status: string; transcriptId: string };
    // First call: submit job + poll. Only THIS call carries the audio URL.
    // `language` forces AssemblyAI's language_code (e.g. 'af') instead of auto-detection —
    // the fix for Afrikaans being detected as Dutch. Omitted → server auto-detects.
    let res: Poll;
    try {
      res = await this.callWithRetry(() => this.postRequest<Poll>('transcribe-assemblyai', {
        audioUrl,
        ...(language ? { language } : {}),
        ...(options.contentHash ? { contentHash: options.contentHash } : {}),
      }), onStatus);
    } catch (submitErr: unknown) {
      if (isJobFailed(submitErr)) throw new TranscriptionFailedError(submitErr.message);
      throw submitErr;
    }

    if (!('transcriptId' in res) || res.status !== 'processing') return res as TranscribeResult;

    const { transcriptId } = res;
    const startedAt = Date.now();
    let delay = POLL_DELAY_START_MS;
    let lastMinute = -1;
    let consecutivePollErrors = 0;
    onStatus(STILL_TRANSCRIBING_TEXT, 'info');

    while (Date.now() - startedAt < ASSEMBLYAI_POLL_MAX_MS) {
      await new Promise(r => setTimeout(r, delay));
      delay = Math.min(Math.round(delay * 1.5), POLL_DELAY_MAX_MS);
      if (options.signal?.aborted) throw new Error('PIPELINE_CANCELLED');

      // Poll-only body: transient errors may be retried, the audio is never re-sent.
      // The job exists and is already paid for at AssemblyAI, so NO error from this phase
      // may reach a caller that would fall back to another engine (= transcribe + charge
      // twice). Keep polling; after repeated failures surface the still-processing message.
      let pollRes: Poll;
      try {
        pollRes = await this.callWithRetry(
          () => this.postRequest<Poll>('transcribe-assemblyai', { transcriptId }),
          onStatus
        );
        consecutivePollErrors = 0;
      } catch (pollErr: unknown) {
        if (pollErr instanceof Error && pollErr.message === 'PIPELINE_CANCELLED') throw pollErr;
        // A dead provider job will never finish: stop polling, do not retry, do not say "still running".
        if (isJobFailed(pollErr)) throw new TranscriptionFailedError(pollErr.message);
        consecutivePollErrors++;
        const reason = pollErr instanceof Error ? pollErr.message : String(pollErr);
        onStatus(`Status check failed (${reason.slice(0, 80)}) — the job is still running, retrying…`, 'warning');
        if (consecutivePollErrors >= POLL_MAX_CONSECUTIVE_ERRORS) {
          throw new TranscriptionStillProcessingError(transcriptId, STILL_PROCESSING_AFTER_ERRORS_TEXT);
        }
        continue;
      }
      if (!('transcriptId' in pollRes) || pollRes.status !== 'processing') {
        return { ...(pollRes as TranscribeResult), assemblyaiId: transcriptId };
      }

      const minutes = Math.floor((Date.now() - startedAt) / 60_000);
      if (minutes !== lastMinute && minutes > 0) {
        lastMinute = minutes;
        onStatus(`${STILL_TRANSCRIBING_TEXT} (${minutes} min elapsed)`, 'info');
      }
    }

    throw new TranscriptionStillProcessingError(
      transcriptId,
      'This recording is still being transcribed after 30 minutes. It has not been resubmitted or charged again — ' +
      'it will appear in History when it finishes (use Complete on the Processing row to fetch it).'
    );
  }

  /**
   * Transcribe audio via Gemini 3.5 Transcribe (purpose-built model, 2.6% WER, 85+ languages).
   * Sends provider='gemini-35' to /api/transcribe-gemini which routes to the Interactions API.
   * NOT used by the client routing: the 2026-09-14 benchmark decision keeps the prompted
   * path (transcribeWithGemini, which runs the repetition and English-leak gates). See
   * docs/decisions/2026-09-14-transcription-engine-benchmark.md.
   */
  async transcribeWithGemini35(
    audioBase64: string,
    mimeType: string,
    onStatus: LogCallback,
    language?: string
  ): Promise<TranscribeResult> {
    return this.callWithRetry(async () => {
      const result = await this.postRequest<TranscribeResult>('transcribe-gemini', {
        audioBase64,
        mimeType,
        provider: 'gemini-35',
        ...(language ? { language } : {}),
      });
      return result;
    }, onStatus);
  }

  /**
   * Transcribe audio from a remote URL via Gemini 3.5 Transcribe.
   * The server downloads the URL, uploads to the Gemini Files API, then calls the
   * Interactions API — same flow as transcribeWithGemini35 but URL-sourced.
   * NOT used by the client routing (see transcribeWithGemini35 and
   * docs/decisions/2026-09-14-transcription-engine-benchmark.md).
   */
  async transcribeFromUrlWithGemini35(
    audioUrl: string,
    onStatus: LogCallback,
    language?: string
  ): Promise<TranscribeResult> {
    return this.callWithRetry(async () => {
      const result = await this.postRequest<TranscribeResult>('transcribe-gemini', {
        audioUrl,
        provider: 'gemini-35',
        ...(language ? { language } : {}),
      });
      return result;
    }, onStatus);
  }

  /**
   * Transcribe audio from URL via Gemini — multilingual with speaker diarisation.
   * For large files (>4MB), use uploadToGemini() first to avoid Vercel timeout.
   */
  async transcribeFromUrlWithGemini(
    audioUrl: string,
    onStatus: LogCallback,
    language?: string
  ): Promise<TranscribeResult> {
    return this.callWithRetry(async () => {
      const result = await this.postRequest<TranscribeResult>('transcribe-gemini', {
        audioUrl,
        ...(language ? { language } : {}),
      });
      return result;
    }, onStatus);
  }

  /**
   * Upload audio from a Supabase signed URL to the Gemini Files API via /api/gemini-upload.
   * Returns the file URI + metadata to pass directly to transcribeFromGeminiFileUri().
   *
   * Split into two calls so each has its own 60s Vercel budget:
   *   1. Upload: download from Supabase + upload to Gemini (~20-40s for 30MB)
   *   2. Transcribe: generation only (~10-20s)
   */
  async uploadToGemini(
    audioUrl: string,
    mimeType: string,
    onStatus: LogCallback
  ): Promise<{ fileUri: string; mimeType: string; fileName: string }> {
    return this.callWithRetry(async () => {
      const result = await this.postRequest<{ fileUri: string; mimeType: string; fileName: string }>(
        'gemini-upload',
        { audioUrl, mimeType }
      );
      return result;
    }, onStatus);
  }

  /**
   * Transcribe using a pre-uploaded Gemini Files API file URI.
   * The generation-only call is fast (<20s) — well within the 60s Vercel budget.
   */
  async transcribeFromGeminiFileUri(
    fileUri: string,
    fileMimeType: string,
    onStatus: LogCallback
  ): Promise<TranscribeResult> {
    return this.callWithRetry(async () => {
      const result = await this.postRequest<TranscribeResult>('transcribe-gemini', {
        fileUri,
        fileMimeType,
      });
      return result;
    }, onStatus);
  }

  /**
   * Transcribe a video file (base64) with Gemini — returns transcript AND visual
   * scene descriptions. For video files ≤4MB sent directly as base64.
   */
  async transcribeVideoWithGemini(
    videoBase64: string,
    mimeType: string,
    onStatus: LogCallback
  ): Promise<TranscribeResult> {
    return this.callWithRetry(async () => {
      const result = await this.postRequest<TranscribeResult>('transcribe-gemini', {
        audioBase64: videoBase64,
        mimeType,
        mediaType: 'video',
      });
      return result;
    }, onStatus);
  }

  /**
   * Transcribe a pre-uploaded video file (Gemini Files API URI) with scene descriptions.
   * Use uploadToGemini() first to get the fileUri — this is the generation-only call.
   */
  async transcribeVideoFromGeminiFileUri(
    fileUri: string,
    fileMimeType: string,
    onStatus: LogCallback
  ): Promise<TranscribeResult> {
    return this.callWithRetry(async () => {
      const result = await this.postRequest<TranscribeResult>('transcribe-gemini', {
        fileUri,
        fileMimeType,
        mediaType: 'video',
      });
      return result;
    }, onStatus);
  }

  /**
   * Delete a file from the Gemini Files API via the server (API key is server-side only).
   * Best-effort — caller should .catch(() => {}) on this.
   */
  async deleteGeminiFile(fileName: string): Promise<void> {
    await this.postRequest<{ ok: boolean }>('gemini-upload', { deleteFileName: fileName });
  }

  /**
   * Translate any non-target-language segments to the user's target language.
   * Always non-fatal — returns the original transcript unchanged on any error.
   * No retry: if Gemini fails, pass-through immediately rather than delaying the user.
   */
  async enforceLanguage(transcript: string, targetLanguage: 'en' | 'af'): Promise<string> {
    try {
      const result = await this.postRequest<{ enforced?: string }>('intelligence', {
        action: 'enforce-language',
        transcript,
        targetLanguage,
      });
      return result.enforced?.trim() || transcript;
    } catch {
      // Pass-through original on any network or API error
      return transcript;
    }
  }

  /**
   * Ultra-fast dictation formatting via AssemblyAI Qwen3.5 4B (~600 ms, $0.10/M tokens).
   * Converts spoken punctuation commands to symbols, fixes capitalisation, normalises numbers.
   * Always non-fatal — returns the original transcript unchanged on any error.
   * No retry: speed is the point; if the model is unavailable, pass-through immediately.
   */
  async formatWithQwen(text: string, language?: string): Promise<string> {
    try {
      const result = await this.postRequest<{ formatted?: string }>('intelligence', {
        action: 'qwen-format',
        text,
        ...(language ? { language } : {}),
      });
      return result.formatted?.trim() || text;
    } catch {
      return text;
    }
  }

  /**
   * Silently correct transcription errors (Dutch→Afrikaans, homophones, mishearings).
   * Always non-fatal — returns the original transcript unchanged on any error.
   * No retry: if Gemini fails, pass-through immediately rather than delaying the user.
   */
  async refineTranscript(transcript: string): Promise<string> {
    try {
      const result = await this.postRequest<{ refined?: string }>('intelligence', {
        action: 'refine',
        transcript,
      });
      return result.refined?.trim() || transcript;
    } catch {
      // Pass-through original on any network or API error
      return transcript;
    }
  }

  /**
   * Generate intelligence synthesis from transcript.
   * Still uses Gemini via /api/intelligence endpoint.
   */
  async generateIntelligence(
    transcript: string,
    instructions: string,
    onStatus: LogCallback
  ): Promise<string> {
    return this.callWithRetry(async () => {
      const result = await this.postRequest<{ text: string }>('intelligence', {
        action: 'generate',
        transcript,
        instructions,
      });
      return result.text || 'Intelligence synthesis failed.';
    }, onStatus);
  }

  /**
   * Generate a specific SPEC-014 intelligence type from transcript.
   * action: 'meeting-minutes' | 'key-decisions' | 'followup-email' | 'action-items-v2'
   * Non-retrying — returns empty object on failure (best-effort, non-fatal).
   */
  async generateIntelligenceType(
    transcript: string,
    action: string,
    outputFormat?: 'json' | 'markdown'
  ): Promise<{ text?: string; structured?: boolean; data?: unknown }> {
    try {
      return await this.postRequest<{ text?: string; structured?: boolean; data?: unknown }>('intelligence', {
        action,
        transcript,
        ...(outputFormat ? { outputFormat } : {}),
      });
    } catch {
      return {};
    }
  }

  /**
   * Re-run any SPEC-014 intelligence type from a saved transcription ID.
   * Server fetches the transcript from DB using the authenticated user's scope.
   */
  async regenerateIntelligence(
    transcriptionId: string,
    intelligenceType: string,
    onStatus: LogCallback
  ): Promise<{ text?: string; structured?: boolean; data?: unknown }> {
    return this.callWithRetry(async () => {
      return await this.postRequest<{ text?: string; structured?: boolean; data?: unknown }>('intelligence', {
        action: 'regenerate',
        transcriptionId,
        intelligenceType,
      });
    }, onStatus);
  }

  /**
   * Save per-type intelligence outputs to the DB via profile/intelligence-save.
   * Best-effort — never throws (non-fatal).
   */
  async saveIntelligence(transcriptionId: string, outputs: Record<string, string>): Promise<void> {
    try {
      await this.postRequest<{ ok: boolean }>('profile/intelligence-save', {
        transcriptionId,
        intelligenceOutputs: outputs,
      });
    } catch {
      // Best-effort — if this fails the in-memory outputs are still available
    }
  }

  /**
   * Generate a structured multilingual meeting summary via Claude.
   *
   * Unlike the SPEC-014 Gemini handlers (which always output British English),
   * The model detects the transcript language and responds in that same language —
   * ideal for Afrikaans, French, German, Zulu, and mixed code-switching meetings.
   *
   * outputFormat 'json' (default): returns MultilingualSummaryResult
   * outputFormat 'markdown':       returns { text: string } (summary_markdown field)
   */
  async multilingualSummary(
    transcript: string,
    outputFormat: 'json' | 'markdown' = 'json'
  ): Promise<MultilingualSummaryResult | { text: string; structured?: false }> {
    return this.postRequest<MultilingualSummaryResult | { text: string; structured?: false }>(
      'intelligence',
      { action: 'multilingual-summary', transcript, outputFormat }
    );
  }
}
